import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download, Sparkles, Undo2 } from "lucide-react";
import { toast } from "sonner";

type ImportType = "tools" | "data_sources" | "software";

interface Candidate {
  name: string;
  exists: boolean;
  sourceProjects: string[];
  meta?: { vendor?: string; category?: string };
}

interface Props {
  type: ImportType;
  orgId: string | null;
  existingNames: string[];
  aiToolCatalog?: { name: string; vendor: string; category: string }[];
  onImport: (items: Candidate[]) => Promise<void>;
}

const SOFTWARE_KEYWORDS = [
  "erp", "crm", "sap", "salesforce", "hubspot", "oracle", "office", "microsoft",
  "google workspace", "workspace", "notion", "slack", "jira", "confluence",
  "power bi", "tableau", "zoho", "odoo", "netsuite", "dynamics", "sharepoint",
  "monday", "asana", "trello", "shopify", "magento", "wordpress",
];

const DATA_SOURCE_KEYWORDS = [
  "base de datos", "bd", "database", "db", "datawarehouse", "warehouse", "datalake", "lake",
  "excel", "csv", "tsv", "hoja de calculo", "spreadsheet", "sheet", "google sheets", "airtable",
  "sql", "mysql", "postgres", "postgresql", "oracle", "mongo", "mongodb", "redis", "snowflake", "bigquery",
  "api", "rest", "graphql", "endpoint", "webhook",
  "erp", "crm", "sap", "salesforce", "hubspot", "dynamics", "netsuite", "odoo", "zoho",
  "sharepoint", "drive", "google drive", "dropbox", "onedrive", "s3", "bucket",
  "sistema", "plataforma", "portal", "aplicacion", "aplicativo",
  "formulario", "form", "encuesta", "survey", "kobo", "typeform",
  "archivo", "file", "pdf", "json", "xml", "parquet",
  "log", "logs", "registro", "historial", "transaccion", "transacciones", "dataset", "tabla", "table",
  "power bi", "tableau", "looker", "metabase", "notion",
];

function stripDiacritics(s: string) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function normalize(s: string) {
  return (s || "").trim().toLowerCase();
}

// Stronger key for dedupe: strip accents, punctuation, collapse spaces, drop trailing plural 's'
function normalizeKey(s: string) {
  let k = stripDiacritics((s || "").toLowerCase())
    .replace(/[.,;:()[\]"'`¡!¿?]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (k.length > 4 && k.endsWith("s")) k = k.slice(0, -1);
  return k;
}

function cleanToken(raw: string): string | null {
  if (!raw) return null;
  const t = raw
    .replace(/^[\s.,;:()[\]"'`¡!¿?\-–—]+|[\s.,;:()[\]"'`¡!¿?\-–—]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length < 3 || t.length > 80) return null;
  if (!/[a-zA-ZÀ-ÿ]/.test(t)) return null; // must contain a letter
  return t;
}

function splitDataUsed(text: string): string[] {
  if (!text) return [];
  return text
    .split(/[\n,;|]+/)
    .map((t) => cleanToken(t))
    .filter((t): t is string => !!t);
}

function hasKeywordWord(token: string, keywords: string[]): boolean {
  const norm = stripDiacritics(token.toLowerCase());
  return keywords.some((kw) => {
    const k = stripDiacritics(kw.toLowerCase());
    const re = new RegExp(`(^|[^a-z0-9])${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`, "i");
    return re.test(norm);
  });
}

function isAcronymLike(token: string): boolean {
  const letters = token.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 2 || letters.length > 12) return false;
  const upper = letters.replace(/[^A-Z]/g, "").length;
  return upper / letters.length >= 0.6;
}


export default function ImportFromProjectsDialog({ type, orgId, existingNames, aiToolCatalog = [], onImport }: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  const existingSet = useMemo(() => new Set(existingNames.map(normalizeKey)), [existingNames]);
  const catalogMap = useMemo(() => {
    const m = new Map<string, { vendor: string; category: string }>();
    aiToolCatalog.forEach((t) => m.set(normalizeKey(t.name), { vendor: t.vendor, category: t.category }));
    return m;
  }, [aiToolCatalog]);

  const projectsQuery = useQuery({
    queryKey: ["import_projects_source", orgId],
    enabled: !!orgId && orgId !== "all" && open,
    queryFn: async () => {
      const { data: projects, error } = await supabase
        .from("participant_projects")
        .select("id, name, data_used, user_id, profiles!participant_projects_user_id_fkey!inner(organization_id)")
        .eq("profiles.organization_id", orgId);
      if (error) throw error;
      const projectIds = (projects || []).map((p: any) => p.id);
      let projectTools: any[] = [];
      if (projectIds.length > 0) {
        const { data: pt } = await supabase.from("project_tools").select("tool_name, project_id").in("project_id", projectIds);
        projectTools = pt || [];
      }
      const { data: solutions } = await supabase
        .from("ai_own_solutions")
        .select("name, tools_used, source_project_id")
        .eq("organization_id", orgId);
      return { projects: projects || [], projectTools, solutions: solutions || [] };
    },
  });

  const candidates = useMemo<Candidate[]>(() => {
    const data = projectsQuery.data;
    if (!data) return [];
    const projNameById = new Map<string, string>();
    data.projects.forEach((p: any) => projNameById.set(p.id, p.name));

    const map = new Map<string, Candidate>();
    const add = (rawName: string, projectName: string, meta?: Candidate["meta"]) => {
      const cleaned = cleanToken(rawName || "");
      if (!cleaned) return;
      const key = normalizeKey(cleaned);
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          name: cleaned,
          exists: existingSet.has(key),
          sourceProjects: [],
          meta: meta || catalogMap.get(key),
        });
      }
      const c = map.get(key)!;
      if (projectName && !c.sourceProjects.includes(projectName)) c.sourceProjects.push(projectName);
    };

    if (type === "tools") {
      data.projectTools.forEach((pt: any) => add(pt.tool_name, projNameById.get(pt.project_id) || ""));
      data.solutions.forEach((s: any) => {
        const pn = s.source_project_id ? projNameById.get(s.source_project_id) || s.name : s.name;
        (s.tools_used || []).forEach((t: string) => add(t, pn));
      });
    } else if (type === "data_sources") {
      data.projects.forEach((p: any) =>
        splitDataUsed(p.data_used).forEach((tok) => {
          if (hasKeywordWord(tok, DATA_SOURCE_KEYWORDS) || isAcronymLike(tok)) {
            add(tok, p.name);
          }
        }),
      );
    } else if (type === "software") {
      // Tokens from data_used that match enterprise software keywords (word-boundary)
      data.projects.forEach((p: any) => {
        splitDataUsed(p.data_used).forEach((tok) => {
          if (hasKeywordWord(tok, SOFTWARE_KEYWORDS)) add(tok, p.name);
        });
      });
      // Tools used in solutions that aren't in the AI catalog → likely enterprise software
      data.solutions.forEach((s: any) => {
        const pn = s.source_project_id ? projNameById.get(s.source_project_id) || s.name : s.name;
        (s.tools_used || []).forEach((t: string) => {
          if (!catalogMap.has(normalizeKey(t)) && hasKeywordWord(t, SOFTWARE_KEYWORDS)) {
            add(t, pn);
          }
        });
      });
    }
    return Array.from(map.values()).sort((a, b) => Number(a.exists) - Number(b.exists) || a.name.localeCompare(b.name));
  }, [projectsQuery.data, type, existingSet, catalogMap]);


  const [refined, setRefined] = useState<Candidate[] | null>(null);
  const [refining, setRefining] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setRefined(null);
    }
  }, [open]);

  // Reset AI refinement when the underlying candidates change (org/type/data change)
  useEffect(() => {
    setRefined(null);
  }, [type, orgId, projectsQuery.data]);

  const visibleCandidates = refined ?? candidates;
  const newCandidates = visibleCandidates.filter((c) => !c.exists);
  const allNewSelected = newCandidates.length > 0 && newCandidates.every((c) => selected.has(c.name));

  const refineWithAI = async () => {
    if (candidates.length === 0) return;
    setRefining(true);
    try {
      const CHUNK = 100;
      const chunks: Candidate[][] = [];
      for (let i = 0; i < candidates.length; i += CHUNK) chunks.push(candidates.slice(i, i + CHUNK));

      const allGroups: { canonical: string; variants: string[]; keep: boolean }[] = [];
      for (const chunk of chunks) {
        const { data, error } = await supabase.functions.invoke("refine-import-candidates", {
          body: {
            type,
            items: chunk.map((c) => ({ name: c.name, sourceProjects: c.sourceProjects })),
          },
        });
        if (error) throw new Error(error.message || "Error IA");
        if (!data?.groups) throw new Error("Respuesta IA inválida");
        allGroups.push(...data.groups);
      }

      // Build lookup from original candidates by normalizeKey and by raw name
      const byKey = new Map<string, Candidate>();
      const byName = new Map<string, Candidate>();
      candidates.forEach((c) => {
        byKey.set(normalizeKey(c.name), c);
        byName.set(c.name, c);
      });
      const keyList = Array.from(byKey.keys());

      const resolveOriginals = (label: string): Candidate[] => {
        if (!label) return [];
        const direct = byName.get(label);
        if (direct) return [direct];
        const key = normalizeKey(label);
        const exact = byKey.get(key);
        if (exact) return [exact];
        // Fallback: partial match on normalized keys
        const matches: Candidate[] = [];
        keyList.forEach((k) => {
          if (k && key && (k.includes(key) || key.includes(k))) {
            const c = byKey.get(k);
            if (c) matches.push(c);
          }
        });
        return matches;
      };

      const unmatched: string[] = [];
      const merged = new Map<string, Candidate>();
      allGroups.forEach((g) => {
        if (!g.keep) return;
        const canonical = (g.canonical || "").trim();
        if (!canonical) return;
        const key = normalizeKey(canonical);
        if (!key) return;
        const sourceProjects: string[] = [];
        const addFrom = (c: Candidate) => {
          c.sourceProjects.forEach((sp) => {
            if (!sourceProjects.includes(sp)) sourceProjects.push(sp);
          });
        };
        // Also pull projects from anything matching the canonical itself
        resolveOriginals(canonical).forEach(addFrom);
        (g.variants || []).forEach((v) => {
          const found = resolveOriginals(v);
          if (found.length === 0) unmatched.push(v);
          else found.forEach(addFrom);
        });
        if (merged.has(key)) {
          const existing = merged.get(key)!;
          sourceProjects.forEach((sp) => {
            if (!existing.sourceProjects.includes(sp)) existing.sourceProjects.push(sp);
          });
        } else {
          merged.set(key, {
            name: canonical,
            exists: existingSet.has(key),
            sourceProjects,
            meta: catalogMap.get(key),
          });
        }
      });
      if (unmatched.length > 0) {
        console.warn("[refine-import] variants sin match:", unmatched);
      }

      const refinedList = Array.from(merged.values()).sort(
        (a, b) => Number(a.exists) - Number(b.exists) || a.name.localeCompare(b.name),
      );
      setRefined(refinedList);
      setSelected(new Set());
      const removed = candidates.length - refinedList.length;
      toast.success(
        `IA consolidó ${candidates.length} → ${refinedList.length}${removed > 0 ? ` (${removed} agrupados/descartados)` : ""}`,
      );
    } catch (e: any) {
      toast.error(e.message || "No se pudo refinar con IA");
    } finally {
      setRefining(false);
    }
  };


  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelected(next);
  };

  const toggleAll = () => {
    if (allNewSelected) setSelected(new Set());
    else setSelected(new Set(newCandidates.map((c) => c.name)));
  };

  const handleImport = async () => {
    if (!orgId || orgId === "all") {
      toast.error("Selecciona una organización específica");
      return;
    }
    const items = visibleCandidates.filter((c) => selected.has(c.name) && !c.exists);
    if (items.length === 0) {
      toast.error("Selecciona al menos un elemento");
      return;
    }
    setImporting(true);
    try {
      await onImport(items);
      const skipped = visibleCandidates.filter((c) => c.exists).length;
      toast.success(`${items.length} importados${skipped ? ` · ${skipped} ya existían` : ""}`);
      setOpen(false);
    } catch (e: any) {
      toast.error(e.message || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const titles: Record<ImportType, string> = {
    tools: "Importar herramientas desde proyectos",
    data_sources: "Importar fuentes de datos desde proyectos",
    software: "Importar aplicaciones desde proyectos",
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Importar desde proyectos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titles[type]}</DialogTitle>
        </DialogHeader>
        {projectsQuery.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : projectsQuery.isError ? (
          <p className="text-sm text-destructive py-8 text-center">
            Error al cargar candidatos: {(projectsQuery.error as any)?.message || "desconocido"}
          </p>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No se encontraron candidatos en los proyectos de esta organización.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 text-sm flex-wrap">
              <span className="text-muted-foreground">
                {refined ? (
                  <>
                    <Sparkles className="inline h-3 w-3 mr-1 text-primary" />
                    IA: {visibleCandidates.length} consolidados ({candidates.length} originales) · {newCandidates.length} nuevos
                  </>
                ) : (
                  <>{newCandidates.length} nuevos · {visibleCandidates.length - newCandidates.length} ya existen</>
                )}
              </span>
              <div className="flex items-center gap-1">
                {(type === "data_sources" || type === "software") && (
                  refined ? (
                    <Button variant="ghost" size="sm" onClick={() => { setRefined(null); setSelected(new Set()); }} disabled={refining || importing}>
                      <Undo2 className="h-3.5 w-3.5 mr-1" /> Deshacer IA
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={refineWithAI} disabled={refining || importing || candidates.length === 0}>
                      {refining ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                      Refinar con IA
                    </Button>
                  )
                )}
                <Button variant="ghost" size="sm" onClick={toggleAll} disabled={newCandidates.length === 0 || refining}>
                  {allNewSelected ? "Deseleccionar todos" : "Seleccionar todos los nuevos"}
                </Button>
              </div>
            </div>

            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Nombre detectado</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead className="text-right">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleCandidates.map((c) => (
                    <TableRow key={c.name} className={c.exists ? "opacity-50" : ""}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(c.name)}
                          onCheckedChange={() => toggle(c.name)}
                          disabled={c.exists}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        {c.meta?.vendor && (
                          <div className="text-xs text-muted-foreground">{c.meta.vendor} · {c.meta.category}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" title={c.sourceProjects.join(", ")}>
                        {refined && c.sourceProjects.length >= 3
                          ? `${c.sourceProjects.length} proyectos`
                          : c.sourceProjects.slice(0, 2).join(", ") +
                            (c.sourceProjects.length > 2 ? ` +${c.sourceProjects.length - 2}` : "")}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.exists ? (
                          <Badge variant="secondary">Ya existe</Badge>
                        ) : (
                          <Badge className="bg-green-500/10 text-green-600">Nuevo</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={importing}>Cancelar</Button>
              <Button onClick={handleImport} disabled={importing || selected.size === 0}>
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Importar ({selected.size})
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export type { Candidate };
